export type MemberSortKey = 'lastName' | 'coverageStatus'
export type SortDirection = 'asc' | 'desc'

/**
 * The sort the roster starts on. Single source of truth shared by the server
 * component's page-0 fetch (MemberRoster) and the client list's initial sort
 * state (MemberList), so the first page and every scroll-appended page request
 * the SAME order. If these two diverge, page 0 and page 1+ become slices of
 * differently-sorted lists and the seam duplicates some members while dropping
 * others. `wire`/`wireDirection` are the enrollment API's expected casing.
 */
export const DEFAULT_MEMBER_SORT: {
  key: MemberSortKey
  direction: SortDirection
  wire: string
  wireDirection: 'ASC' | 'DESC'
} = {
  key: 'lastName',
  direction: 'asc',
  wire: 'lastName',
  wireDirection: 'ASC',
}

/**
 * Coverage-status vocabulary. Note that EmployeeSummary.coverageStatus is still
 * typed `string` because the value originates from the enrollment API; this
 * union constrains the values the UI knows how to rank and filter, not the wire
 * type.
 */
export type CoverageStatus = 'ACTIVE' | 'UPCOMING' | 'TERMINATED'

// Single source of truth for the coverage vocabulary AND its lifecycle display
// order: active-first, then upcoming, terminated. COVERAGE_STATUSES is derived
// from these keys, so adding a status here updates the whitelist and the chip
// options too. Typed as a complete (non-Partial) record because all three
// statuses are always present. The ranks encode the canonical status ordering
// (also the server sort's expected order); they are not consumed for a
// client-side sort now that the roster is server-presorted.
const STATUS_RANK: Record<CoverageStatus, number> = {
  ACTIVE: 0,
  UPCOMING: 1,
  TERMINATED: 2,
}

// Derived from STATUS_RANK so the two cannot drift. Object key order is
// insertion order for string keys, so this preserves the lifecycle order above.
export const COVERAGE_STATUSES: readonly CoverageStatus[] = Object.keys(
  STATUS_RANK,
) as CoverageStatus[]

/**
 * Validates a raw `coverage` URL param against the known status vocabulary.
 * Returns the value when it is a recognized status, otherwise '' (no filter).
 * Guards against a hand-edited or stale URL like ?coverage=INVALID rendering a
 * deselected chip alongside a silently empty list.
 *
 * @param raw - The raw URL param value (may be undefined)
 * @returns A known CoverageStatus, or '' when unrecognized
 */
export function sanitizeCoverageParam(
  raw: string | undefined,
): CoverageStatus | '' {
  return raw && (COVERAGE_STATUSES as readonly string[]).includes(raw)
    ? (raw as CoverageStatus)
    : ''
}
